// src/pages/LogExercise.jsx
/* LogExercise page */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import ExerciseFlowSteps from "../components/ExerciseFlowSteps";
import RestTimerButton from "../components/RestTimerButton";
import SeoHead from "../components/SeoHead";
import { getSetsByExercise, getPastSetsByExercise, updateSetsByExercise } from "../services/exerciseService";
import "./LogExercise.css";
import "../styles/Common.css";
import { pullAndMergeExerciseLog, pushExerciseLog } from '../services/syncService';
import { todayYMDLocal } from "../utils/dateUtils";
import { useAuthCompat } from "../utils/clerkConfig";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseNameById } from "../utils/exerciseName";
import { getExerciseDetails } from "../services/apiClient";
import { trackEvent } from "../utils/analytics";

export default function LogExercise() {
    const { t, localizePath } = useI18n();
    const { exerciseId, date } = useParams();
    const navigate = useNavigate();
    const today = todayYMDLocal();
    const { getToken } = useAuthCompat();
    const { language } = useI18n();

    // Initialize sets state with an empty array
    const [sets, setSets] = useState([]);
    const [statusType, setStatusType] = useState("done");
    const [activeSetIndex, setActiveSetIndex] = useState(null);
    const [pastLogs, setPastLogs] = useState([]);
    const [saveError, setSaveError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(date || today);
    const [originalDate, setOriginalDate] = useState(date || today);
    const [hasDetails, setHasDetails] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(true);

    useEffect(() => {
        let mounted = true;
        async function checkDetails() {
            setLoadingDetails(true);
            try {
                const data = await getExerciseDetails(getToken, exerciseId, language);
                if (mounted && data && Object.keys(data).length > 0) {
                    setHasDetails(true);
                } else if (mounted) {
                    setHasDetails(false);
                }
            } catch (err) {
                if (mounted) {
                    setHasDetails(false);
                }
            } finally {
                if (mounted) {
                    setLoadingDetails(false);
                }
            }
        }
        checkDetails();
        return () => { mounted = false; };
    }, [exerciseId, language, getToken]);

    const autoSaveTimeoutRef = useRef(null);
    const hasInitializedAutoSaveRef = useRef(false);
    const isDirtyRef = useRef(false);
    const latestMetaRef = useRef({
        selectedDate,
        originalDate,
        exerciseId,
        statusType,
    });


    // Function to initialize sets with data from localStorage
    const initializeSets = (date, exId) => {
        const initialSets = getSetsByExercise(date, exId);
        const hasSets = initialSets.length > 0;
        const allArePlanned = hasSets && initialSets.every((s) => s && s.type === "planned");
        const detectedStatus = allArePlanned ? "planned" : "done";
        setStatusType(detectedStatus);
        latestMetaRef.current.statusType = detectedStatus;
        const emptyRow = detectedStatus === "planned" ? { weight: "", reps: "", type: "planned" } : { weight: "", reps: "" };
        return hasSets
            ? [...initialSets, emptyRow]
            : [emptyRow];
    };

    // Build list of recent logs (declared early to satisfy lint rules)
    const fetchPastLogs = useCallback(() => {
        if (!selectedDate) {
            setPastLogs([]);
            return;
        }

        setPastLogs(getPastSetsByExercise(exerciseId, selectedDate, 10));
    }, [selectedDate, exerciseId]);

    // Initialize sets on component mount and when exerciseId changes
    // We don't include selectedDate in the dependency array to prevent reloading sets when date changes
    useEffect(() => {
        // Only initialize sets from storage when component mounts or exerciseId changes
        // This prevents losing sets data when only the date changes
        setSets(initializeSets(originalDate, exerciseId));
        fetchPastLogs();
    }, [exerciseId, originalDate, fetchPastLogs]);
    
    // Separate effect for fetching past logs when selectedDate changes
    useEffect(() => {
        fetchPastLogs();
    }, [selectedDate, fetchPastLogs]);

    useEffect(() => {
        latestMetaRef.current = {
            selectedDate,
            originalDate,
            exerciseId,
            statusType,
        };
    }, [selectedDate, originalDate, exerciseId, statusType]);

    const isSetSavable = (set) => {
        const reps = parseInt(set.reps, 10);
        const hasValidReps = set.reps !== "" && !Number.isNaN(reps) && reps > 0;

        // Weight is considered valid if it's a non-negative number OR empty (treated as 0)
        const hasValidWeightNumber = set.weight !== "" && !Number.isNaN(parseFloat(set.weight)) && parseFloat(set.weight) >= 0;
        const isEmptyWeight = set.weight === "";

        return hasValidReps && (hasValidWeightNumber || isEmptyWeight);
    };

    const isSetRowPlanned = (setItem) => {
        return Boolean(setItem && setItem.type === "planned");
    };

    const normalizeSetsForSave = (rawSets) =>
        rawSets.map((s) => {
            const reps = parseInt(s.reps, 10);
            const hasValidReps = s.reps !== "" && !Number.isNaN(reps) && reps > 0;
            // If reps are valid and weight is empty, coerce weight to "0" so it persists
            let updated = (hasValidReps && (s.weight === "" || Number.isNaN(parseFloat(s.weight))))
                ? { ...s, weight: "0" }
                : { ...s };

            if (s.type === "planned") {
                return { ...updated, type: "planned" };
            }
            const { type, ...rest } = updated;
            return rest;
        });

    useEffect(() => {
        if (!hasInitializedAutoSaveRef.current) {
            hasInitializedAutoSaveRef.current = true;
            return;
        }

        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        autoSaveTimeoutRef.current = setTimeout(async () => {
            const { selectedDate: currentDate, originalDate: currentOriginalDate, exerciseId: currentExerciseId, statusType: currentStatusType } = latestMetaRef.current;

            if (!currentDate || !/^\d{4}-\d{2}-\d{2}$/.test(currentDate) || !isDirtyRef.current) {
                return;
            }

            const normalized = normalizeSetsForSave(sets);
            // Pull remote first and merge to include changes from other devices
            try { await pullAndMergeExerciseLog(getToken); } catch (_) { /* ignore network/auth errors for UX */ }

            const saveResult = updateSetsByExercise(
                currentDate,
                currentExerciseId,
                normalized,
                currentDate !== currentOriginalDate ? currentOriginalDate : null
            );

            if (!saveResult) {
                setSaveError(t("log.autosaveFailed"));
                return;
            }

            isDirtyRef.current = false;

            // Push the full log to API in the background (best-effort)
            try { await pushExerciseLog(getToken); } catch (_) { /* ignore network/auth errors for UX */ }

            setSaveError(null);

            if (currentDate !== currentOriginalDate) {
                setOriginalDate(currentDate);
            }
        }, 500);

        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
                // Flush pending changes before unmount so changes are never lost
                const { selectedDate: currentDate, originalDate: currentOriginalDate, exerciseId: currentExerciseId, statusType: currentStatusType } = latestMetaRef.current;
                if (currentDate && /^\d{4}-\d{2}-\d{2}$/.test(currentDate) && currentExerciseId && isDirtyRef.current) {
                    const normalized = normalizeSetsForSave(sets);
                    updateSetsByExercise(
                        currentDate,
                        currentExerciseId,
                        normalized,
                        currentDate !== currentOriginalDate ? currentOriginalDate : null
                    );
                    isDirtyRef.current = false;
                }
            }
        };
    }, [getToken, sets, statusType, t]);

    const handleStatusTypeChange = (newType) => {
        setStatusType(newType);
        latestMetaRef.current.statusType = newType;
        const updatedSets = sets.map((s) => {
            if (newType === "planned") {
                return { ...s, type: "planned" };
            }
            const { type, ...rest } = s;
            return rest;
        });
        setSets(updatedSets);

        // Immediately persist to storage so navigation or back link never loses the planned change
        const { selectedDate: currentDate, originalDate: currentOriginalDate, exerciseId: currentExerciseId } = latestMetaRef.current;
        if (currentDate && currentExerciseId) {
            const normalized = normalizeSetsForSave(updatedSets);
            updateSetsByExercise(
                currentDate,
                currentExerciseId,
                normalized,
                currentDate !== currentOriginalDate ? currentOriginalDate : null
            );
            isDirtyRef.current = false;
            try {
                const p = pushExerciseLog(getToken);
                if (p && typeof p.catch === "function") {
                    p.catch(() => {});
                }
            } catch (_) {}
        }
    };

    const handleToggleSetType = (index) => {
        const next = [...sets];
        const current = next[index];
        const isCurrentlyPlanned = current?.type === "planned";
        if (isCurrentlyPlanned) {
            const { type, ...rest } = current;
            next[index] = rest;
        } else {
            next[index] = { ...current, type: "planned" };
        }

        const savable = next.filter(isSetSavable);
        const allPlanned = savable.length > 0 && savable.every((s) => s.type === "planned");
        const allDone = savable.length > 0 && savable.every((s) => !s.type || s.type !== "planned");
        let nextStatus = "mixed";
        if (allPlanned) {
            nextStatus = "planned";
        } else if (allDone) {
            nextStatus = "done";
        }
        setStatusType(nextStatus);
        latestMetaRef.current.statusType = nextStatus;
        setSets(next);

        // Immediately persist to storage
        const { selectedDate: currentDate, originalDate: currentOriginalDate, exerciseId: currentExerciseId } = latestMetaRef.current;
        if (currentDate && currentExerciseId) {
            const normalized = normalizeSetsForSave(next);
            updateSetsByExercise(
                currentDate,
                currentExerciseId,
                normalized,
                currentDate !== currentOriginalDate ? currentOriginalDate : null
            );
            isDirtyRef.current = false;
            try {
                const p = pushExerciseLog(getToken);
                if (p && typeof p.catch === "function") {
                    p.catch(() => {});
                }
            } catch (_) {}
        }
    };

    const handleChange = (e, index) => {
        const { name, value } = e.target;
        setActiveSetIndex(index);
        isDirtyRef.current = true;

        const newSets = [...sets];
        newSets[index] = { ...newSets[index], [name]: value };

        const isLastSet = index === newSets.length - 1;
        const weight = parseFloat(newSets[index].weight);
        const reps = parseInt(newSets[index].reps, 10);
        const isSetComplete =
            newSets[index].weight !== "" && !Number.isNaN(weight) && weight >= 0 &&
            newSets[index].reps !== "" && !Number.isNaN(reps) && reps > 0;

        if (isLastSet && isSetComplete) {
            const nextSet = statusType === "planned" ? { weight: "", reps: "", type: "planned" } : { weight: "", reps: "" };
            setSets([...newSets, nextSet]);
        } else {
            setSets(newSets);
        }
    };

    const handleDelete = (index) => {
        isDirtyRef.current = true;
        const newSets = sets.filter((_, i) => i !== index);

        if (newSets.length === 0 || newSets.every((set) => set.weight && set.reps)) {
            newSets.push(statusType === "planned" ? { weight: "", reps: "", type: "planned" } : { weight: "", reps: "" });
        }

        setSets(newSets);
        setActiveSetIndex(null);
    };

    // Compute the last filled (savable) row index for the Repeat Set action
    const lastSavableIndex = (() => {
        let idx = -1;
        for (let i = 0; i < sets.length; i++) {
            if (isSetSavable(sets[i])) idx = i;
        }
        return idx;
    })();
    const lastSavableSet = lastSavableIndex !== -1 ? sets[lastSavableIndex] : null;

    const handleCopyLast = () => {
        isDirtyRef.current = true;
        setSets((prev) => {
            if (!prev || prev.length === 0) return prev;
            let srcIndex = -1;
            for (let i = 0; i < prev.length; i++) {
                if (isSetSavable(prev[i])) srcIndex = i;
            }
            if (srcIndex < 0) return prev;

            const source = prev[srcIndex];
            // Only copy if the previous row is savable (complete)
            const reps = parseInt(source?.reps, 10);
            const hasValidReps = source?.reps !== "" && !Number.isNaN(reps) && reps > 0;
            const hasValidWeightNumber = source?.weight !== "" && !Number.isNaN(parseFloat(source?.weight)) && parseFloat(source?.weight) >= 0;
            const isEmptyWeight = source?.weight === ""; // empty weight allowed, saved as 0 later
            const canCopy = hasValidReps && (hasValidWeightNumber || isEmptyWeight);
            if (!canCopy) return prev;

            const isSourcePlanned = source?.type === "planned";
            const copied = {
                weight: String(source.weight ?? ""),
                reps: String(source.reps ?? ""),
                ...(isSourcePlanned ? { type: "planned" } : {}),
            };
            const next = [...prev];
            const lastIndex = next.length - 1;
            // Fill the last (empty) row with copied values, or push if already complete
            if (lastIndex >= 0 && !isSetSavable(next[lastIndex])) {
                next[lastIndex] = copied;
            } else {
                next.push(copied);
            }
            next.push(statusType === "planned" ? { weight: "", reps: "", type: "planned" } : { weight: "", reps: "" });
            setActiveSetIndex(next.length - 1);
            return next;
        });
    };

    const handleCopyPastSet = (log) => {
        if (!log) return;
        isDirtyRef.current = true;
        const copiedWeight = log.weight !== undefined && log.weight !== null ? String(log.weight) : "";
        const copiedReps = log.reps !== undefined && log.reps !== null ? String(log.reps) : "";

        setSets((prev) => {
            const next = prev && prev.length > 0 ? [...prev] : [{ weight: "", reps: "" }];

            // Determine target index:
            // If the user has focused/edited a specific set, copy into that set.
            // Otherwise, find the first empty row (both weight and reps empty),
            // or default to the last row.
            let targetIndex = activeSetIndex;
            if (targetIndex === null || targetIndex < 0 || targetIndex >= next.length) {
                const emptyIndex = next.findIndex((s) => s.weight === "" && s.reps === "");
                targetIndex = emptyIndex !== -1 ? emptyIndex : next.length - 1;
            }

            next[targetIndex] = {
                weight: copiedWeight,
                reps: copiedReps,
            };

            const isLastSet = targetIndex === next.length - 1;
            const isComplete = isSetSavable(next[targetIndex]);

            if (isLastSet && isComplete) {
                next.push({ weight: "", reps: "" });
                setActiveSetIndex(next.length - 1);
            } else {
                setActiveSetIndex(targetIndex);
            }

            return next;
        });
    };

    // Note: +/- adjust buttons removed for layout stability across devices.

    const saveSets = async () => {
        try {
            // Clear any previous error
            setSaveError(null);
            
            // Validate date format (YYYY-MM-DD)
            const datePattern = /^\d{4}-\d{2}-\d{2}$/;
            if (!datePattern.test(selectedDate)) {
                setSaveError(t("log.invalidDate"));
                return;
            }
            
            isDirtyRef.current = false;
            const normalized = normalizeSetsForSave(sets);
            const completeSets = normalized.filter(isSetSavable);

            // Check if the date has changed
            const dateHasChanged = selectedDate !== originalDate;
            
            // If the date has changed, pass the original date to updateSetsByExercise
            // This will remove the exercise from the original date's log
            // If Clerk is enabled, pull remote first and merge into local to include other devices
            try { await pullAndMergeExerciseLog(getToken); } catch (_) { /* ignore network/auth errors for UX */ }

            const saveResult = updateSetsByExercise(
                selectedDate, 
                exerciseId, 
                completeSets,
                dateHasChanged ? originalDate : null
            );
            
            if (saveResult) {
                trackEvent("log_exercise", {
                    exercise_id: exerciseId,
                    sets_count: completeSets.length,
                    workout_type: statusType,
                });
                // After local save, push the full log to API if available
                try { await pushExerciseLog(getToken); } catch (_) { /* ignore network/auth errors for UX */ }
                navigate(localizePath("/"));
            } else {
                setSaveError(t("log.saveFailed"));
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Save sets failed:", error);
            setSaveError(t("log.saveFailed"));
        }
    };

    // Function to get the formatted exercise name for UI (Title Case)
    const formatExerciseName = (id) => {
        return id
            .replace(/_/g, " ")
            .split(" ")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };
    const formattedExerciseName = getLocalizedExerciseNameById(
        exerciseId,
        t,
        formatExerciseName(exerciseId)
    );

    return (
        <div className="logExercisePage pageContainer">
            <SeoHead
                title={t("log.seoTitle", { exercise: formattedExerciseName })}
                description={t("log.seoDescription", { exercise: formattedExerciseName })}
                canonicalPath={localizePath(`/LogExercise/${exerciseId}`)}
                noIndex={true}
            />
            <Breadcrumbs current={t("breadcrumbs.logExerciseSection")} />
            <Link to={localizePath("/")} className="flowBackLink appTextLink">
                <span className="flowBackLinkIcon" aria-hidden="true">←</span>
                <span className="flowBackLinkLabel">{t("common.back")}</span>
            </Link>
            <div className="logExerciseIntro">
                <ExerciseFlowSteps activeStep={2} />
                <h1 className="sectionHeading">
                    {t("log.title", { exercise: formattedExerciseName })}
                </h1>
                <div className="exerciseDetailsSlot" aria-live="polite">
                    {loadingDetails ? (
                        <div className="exerciseDetailsSkeleton" aria-hidden="true" />
                    ) : hasDetails ? (
                        <Link
                            to={localizePath(`/exercises/${exerciseId}`)}
                            className="exerciseDetailsButton appButton appButton--secondary"
                            id="exerciseDetailsBtn"
                            title={t("log.detailsAbout", { exercise: formattedExerciseName })}
                        >
                            <span className="exerciseDetailsIcon" aria-hidden="true">ℹ</span>
                            <span className="exerciseDetailsLabel">
                                {t("log.detailsAbout", { exercise: formattedExerciseName })}
                            </span>
                        </Link>
                    ) : null}
                </div>
            </div>

            <div className="formContainer logExerciseCard">
                <div className="logExerciseCardHeader">
                    <div className="logWorkoutTypeToggle" role="radiogroup" aria-label={t("log.statusLabel")}>
                        <button
                            type="button"
                            role="radio"
                            id="logWorkoutTypeDone"
                            aria-checked={statusType === "done"}
                            className={`logWorkoutTypeBtn logWorkoutTypeBtn--done ${statusType === "done" ? "logWorkoutTypeBtn--active" : ""}`}
                            onClick={() => handleStatusTypeChange("done")}
                        >
                            <span className="logWorkoutTypeBtnIcon" aria-hidden="true">✓</span>
                            <span className="logWorkoutTypeBtnText">{t("log.done")}</span>
                        </button>
                        <button
                            type="button"
                            role="radio"
                            id="logWorkoutTypePlanned"
                            aria-checked={statusType === "planned"}
                            className={`logWorkoutTypeBtn logWorkoutTypeBtn--planned ${statusType === "planned" ? "logWorkoutTypeBtn--active" : ""}`}
                            onClick={() => handleStatusTypeChange("planned")}
                        >
                            <span className="logWorkoutTypeBtnIcon" aria-hidden="true">📋</span>
                            <span className="logWorkoutTypeBtnText">{t("log.planned")}</span>
                        </button>
                    </div>
                    <RestTimerButton />
                </div>

                {sets.length === 0 ? (
                    <p>{t("log.noSets")}</p>
                ) : (
                    <div className="setsTableWrap">
                        <table className="setsTable">
                            <thead>
                            <tr>
                                <th className="col-status">{t("log.set")}</th>
                                <th className="col-weight">{t("log.weight")}</th>
                                <th className="col-reps" title={t("log.repsRequiredTitle")}>
                                    {t("log.reps")}{" "}
                                    <span className="repsRequiredMark" aria-hidden="true">*</span>
                                </th>
                                <th className="col-delete">{t("log.actions")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {sets.map((setItem, index) => {
                                const isComplete = isSetSavable(setItem);
                                const isPlanned = isSetRowPlanned(setItem);
                                return (
                                    <tr key={index}>
                                        <td className="col-status">
                                            <div className="setCell">
                                                {isComplete ? (
                                                    <button
                                                        type="button"
                                                        className={`appButton statusBtn setStatusToggleButton ${isPlanned ? "statusBtn--planned setStatusToggleButton--planned" : "statusBtn--done setStatusToggleButton--done"}`}
                                                        aria-label={isPlanned ? t("log.markAsDone") : t("log.markAsPlanned")}
                                                        title={isPlanned ? t("log.markAsDone") : t("log.markAsPlanned")}
                                                        onClick={() => handleToggleSetType(index)}
                                                    >
                                                        <span className="statusGlyph" aria-hidden="true">{isPlanned ? "📋" : "✓"}</span>
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        disabled
                                                        className="appButton statusBtn statusBtn--draft"
                                                        aria-hidden="true"
                                                        tabIndex={-1}
                                                    >
                                                        <span className="statusGlyph" aria-hidden="true">○</span>
                                                    </button>
                                                )}
                                                <span className="setNumLabel">{index + 1}</span>
                                            </div>
                                        </td>
                                        <td className="col-weight">
                                            <input
                                                type="number"
                                                name="weight"
                                                placeholder={t("log.weight")}
                                                value={setItem.weight}
                                                onChange={(e) => handleChange(e, index)}
                                                onFocus={() => setActiveSetIndex(index)}
                                                className="input"
                                                min="0"
                                            />
                                        </td>
                                        <td className="col-reps">
                                            <input
                                                type="number"
                                                name="reps"
                                                placeholder={t("log.reps")}
                                                value={setItem.reps}
                                                onChange={(e) => handleChange(e, index)}
                                                onFocus={() => setActiveSetIndex(index)}
                                                className="input"
                                                min="0"
                                            />
                                        </td>
                                        <td className="col-delete">
                                            <div className="actionsCell">
                                                {isComplete && (
                                                    <button
                                                        className="deleteButton appButton appButton--danger"
                                                        aria-label={t("log.deleteAria")}
                                                        title={t("log.delete")}
                                                        onClick={() => handleDelete(index)}
                                                    >
                                                        <span aria-hidden="true">×</span>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                )}

                {lastSavableSet && (
                    <div className="bottomActionBar">
                        <button
                            type="button"
                            className="appButton cloneLastSetBtn"
                            id="repeatLastSetBtn"
                            aria-label={t("log.repeatSetAria", {
                                setNumber: lastSavableIndex + 1,
                                details: `${lastSavableSet.weight !== "" ? lastSavableSet.weight : "0"} × ${lastSavableSet.reps}`
                            })}
                            title={t("log.repeatSetAria", {
                                setNumber: lastSavableIndex + 1,
                                details: `${lastSavableSet.weight !== "" ? lastSavableSet.weight : "0"} × ${lastSavableSet.reps}`
                            })}
                            onClick={handleCopyLast}
                        >
                            <span className="cloneIcon" aria-hidden="true">⧉</span>
                            <span className="cloneLabel">
                                {t("log.repeatSet", { setNumber: lastSavableIndex + 1 })}
                            </span>
                            <span className="cloneHighlight">
                                {`${lastSavableSet.weight !== "" ? lastSavableSet.weight : "0"} × ${lastSavableSet.reps}`}
                            </span>
                        </button>
                    </div>
                )}
                
                {saveError && (
                    <div className="errorMessage">
                        <strong>{t("log.errorPrefix")}</strong> {saveError}
                    </div>
                )}
                
                <div className="formFooter">
                    <div className="dateInputContainer">
                        <label htmlFor="exerciseDate" className="dateInputLabel">{t("log.dateLabel")}</label>
                        <input
                            id="exerciseDate"
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="dateInput"
                            pattern="\d{4}-\d{2}-\d{2}"
                            required
                        />
                    </div>
                    
                    <button type="button" onClick={saveSets} className="saveButton appButton">
                        {t("log.save")}
                    </button>
                </div>
            </div>

            {/* Table with the last 10 exercise sessions */}
            {pastLogs.length > 0 && (
                <div className="pastLogsContainer logExerciseCard">
                    <h2 className="sectionHeading">{t("log.lastSessions", { count: pastLogs.length })}</h2>
                    <div className="pastLogsTableWrap">
                        <table className="pastLogsTable">
                            <thead>
                            <tr>
                                <th>{t("log.date")}</th>
                                <th>{t("log.weight")}</th>
                                <th>{t("log.reps")}</th>
                                <th>{t("log.actions")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {pastLogs.map((log, index) => (
                                <tr key={index}>
                                    <td>{log.date}</td>
                                    <td>{log.weight}</td>
                                    <td>{log.reps}</td>
                                    <td>
                                        <div className="actionsCell">
                                            <button
                                                type="button"
                                                className="appButton copyPastSetButton"
                                                aria-label={t("log.copyPastSetAria", {
                                                    date: log.date,
                                                    weight: log.weight,
                                                    reps: log.reps,
                                                })}
                                                title={t("log.copyPastSet")}
                                                onClick={() => handleCopyPastSet(log)}
                                            >
                                                <span aria-hidden="true">⧉</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
